import { Swap } from '@hendrx/icons/Swap';
import { ActionIcon, Box, Divider, Flex } from '@mantine/core';
import { useEffect, useState } from 'react';

import classes from './ColorPickerGradient.module.scss';
import { getGradient } from './utils/getGradient';
import { ColorInputSize, ColorSwatchSize, GradientDirection } from '../../../types/ColorPicker';
import { extractGradientColors } from '../../../utils/extractGradientColors';
import { ColorInputPopover } from '../../inputs/ColorInputPopover';
import { ColorSwatch } from '../ColorSwatch';

const DEFAULT_COLORS = ['#AA00FF', '#0500FF'];

type ColorPickerGradientProps = {
  color: string;
  onSelectColor: (color: string) => void;
  themeColors: string[];
};

export function ColorPickerGradient(props: ColorPickerGradientProps) {
  const { color, onSelectColor, themeColors } = props;
  const [colors, setColors] = useState([DEFAULT_COLORS[0], DEFAULT_COLORS[1]]);
  const [gradientDirection, setGradientDirection] = useState<GradientDirection | null>(null);

  useEffect(() => {
    const colors = extractGradientColors(color);
    setColors([colors?.color1 || DEFAULT_COLORS[0], colors?.color2 || DEFAULT_COLORS[1]]);
  }, [color]);

  const updateGradient = ({
    color1,
    color2,
    direction
  }: {
    color1: string;
    color2: string;
    direction: GradientDirection | null;
  }) => {
    if (direction) {
      const newGradient = getGradient({ color1, color2, direction });
      onSelectColor(newGradient);
    }
  };

  const handleColorChange = ({ newColor, index }: { newColor: string; index: number }) => {
    const newColors = [...colors];
    newColors[index] = newColor;
    setColors(newColors);
    updateGradient({ color1: newColors[0], color2: newColors[1], direction: gradientDirection });
  };

  const handleDirectionChange = (newDirection: GradientDirection) => {
    setGradientDirection(newDirection);
    updateGradient({ color1: colors[0], color2: colors[1], direction: newDirection });
  };

  const handleSwap = () => {
    const [color1, color2] = colors;
    setColors([color2, color1]);
    updateGradient({ color1: color2, color2: color1, direction: gradientDirection });
  };

  return (
    <Flex className={classes.wrapper}>
      <Flex className={classes.inputsWrapper}>
        <ColorInputPopover
          color={colors[0]}
          onSelectColor={colorObject => handleColorChange({ newColor: colorObject.color, index: 0 })}
          themeColors={themeColors}
          size={ColorInputSize.small}
        />
        <ActionIcon variant="default" onClick={handleSwap} classNames={{ root: classes.swap }}>
          <Swap />
        </ActionIcon>
        <ColorInputPopover
          color={colors[1]}
          onSelectColor={colorObject => handleColorChange({ newColor: colorObject.color, index: 1 })}
          themeColors={themeColors}
          size={ColorInputSize.small}
        />
      </Flex>
      <Divider className={classes.divider} />
      <Flex className={classes.colorSwatchesWrapper}>
        {Object.values(GradientDirection).map((direction, index) => {
          const gradient = getGradient({ color1: colors[0], color2: colors[1], direction });
          return (
            <Box key={index} onClick={() => handleDirectionChange(direction)} className={classes.colorSwatch}>
              <ColorSwatch
                color={gradient}
                selected={gradient.toLowerCase() === color.toLowerCase()}
                size={ColorSwatchSize.large}
              />
            </Box>
          );
        })}
      </Flex>
    </Flex>
  );
}
